import { AxiosError } from 'axios';

export interface DetailedError {
  title: string;
  message: string;
  details?: string;
  suggestions?: string[];
  type: 'network' | 'auth' | 'cors' | 'server' | 'unknown';
}

export function getDetailedErrorMessage(error: any): DetailedError {
  console.error('Detailed error analysis:', error);

  // Axios Error 處理
  if (error?.isAxiosError) {
    const axiosError = error as AxiosError;

    // 網路錯誤
    if (axiosError.code === 'ERR_NETWORK' || axiosError.message === 'Network Error') {
      return {
        title: '網路連線錯誤',
        message: '無法連接到伺服器',
        details: `錯誤代碼: ${axiosError.code}`,
        suggestions: [
          '檢查您的網路連線',
          '確認 API 伺服器 (chatfile-api.hungda.net) 是否正常運行',
          '嘗試重新整理頁面',
          '若問題持續，請聯繫系統管理員'
        ],
        type: 'network'
      };
    }

    // CORS 錯誤
    if (axiosError.message.includes('CORS') ||
        axiosError.message.includes('Access-Control-Allow-Origin')) {
      return {
        title: 'CORS 跨域請求錯誤',
        message: '瀏覽器阻止了跨域請求',
        details: `來源域名: ${window.location.origin}`,
        suggestions: [
          '這通常是伺服器配置問題',
          '請確認 API 伺服器已允許此域名的請求',
          '嘗試清除瀏覽器快取',
          '聯繫系統管理員檢查 CORS 設定'
        ],
        type: 'cors'
      };
    }

    // HTTP 狀態碼錯誤
    if (axiosError.response) {
      const status = axiosError.response.status;
      const data = axiosError.response.data as any;

      switch (status) {
        case 401:
          const authErrorMsg = data?.error || data?.message || '請重新登入';
          let authSuggestions = [
            '請使用授權的 Google 帳號重新登入',
            '確認您的帳號在授權清單中',
            '檢查登入狀態是否過期'
          ];

          // Specific guidance based on error type
          if (authErrorMsg.includes('No token provided')) {
            authSuggestions = [
              '您似乎未登入，請先進行 Google 登入',
              '如果已登入，請重新整理頁面',
              '檢查瀏覽器是否支援 Firebase Authentication'
            ];
          } else if (authErrorMsg.includes('Token expired')) {
            authSuggestions = [
              '您的登入已過期，請重新登入',
              '清除瀏覽器快取後重新登入',
              '確保系統時間正確'
            ];
          } else if (authErrorMsg.includes('Invalid token format')) {
            authSuggestions = [
              '認證 token 格式不正確，請重新登入',
              '清除瀏覽器快取和 Cookie',
              '檢查網路連線穩定性',
              '如果問題持續，請聯繫技術支援'
            ];
          }

          return {
            title: '身份驗證失敗',
            message: '您沒有權限訪問此資源',
            details: authErrorMsg,
            suggestions: authSuggestions,
            type: 'auth'
          };

        case 403:
          return {
            title: '訪問被拒絕',
            message: '您的帳號權限不足',
            details: data?.message || data?.error || '需要管理員權限',
            suggestions: [
              '確認您的帳號具有適當權限',
              '聯繫系統管理員申請權限',
              '檢查角色設定是否正確'
            ],
            type: 'auth'
          };

        case 404:
          return {
            title: 'API 端點不存在',
            message: '請求的資源未找到',
            details: `URL: ${axiosError.config?.url}`,
            suggestions: [
              '檢查 API 版本是否正確',
              '確認後端服務是否已部署',
              '聯繫開發團隊確認 API 路徑'
            ],
            type: 'server'
          };

        case 500:
          return {
            title: '伺服器內部錯誤',
            message: '後端服務發生錯誤',
            details: data?.message || data?.error || '伺服器處理請求時出現問題',
            suggestions: [
              '稍後再試',
              '檢查伺服器日誌',
              '聲告系統管理員'
            ],
            type: 'server'
          };

        case 502:
        case 503:
        case 504:
          return {
            title: '服務暫時不可用',
            message: `伺服器回應錯誤 (${status})`,
            details: '可能是伺服器維護或過載',
            suggestions: [
              '稍後再試',
              '檢查伺服器狀態',
              '聯繫系統管理員'
            ],
            type: 'server'
          };

        default:
          return {
            title: `HTTP 錯誤 ${status}`,
            message: data?.message || data?.error || '未知的伺服器錯誤',
            details: `狀態碼: ${status}`,
            suggestions: [
              '檢查請求參數',
              '查看伺服器日誌',
              '聯繫技術支援'
            ],
            type: 'server'
          };
      }
    }

    // 請求超時
    if (axiosError.code === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
      return {
        title: '請求超時',
        message: '伺服器響應時間過長',
        details: `超時設定: ${axiosError.config?.timeout || '預設'}ms`,
        suggestions: [
          '檢查網路連線速度',
          '稍後再試',
          '聯繫系統管理員檢查伺服器效能'
        ],
        type: 'network'
      };
    }
  }

  // Firebase Auth 錯誤
  if (error?.code?.startsWith?.('auth/')) {
    return {
      title: 'Firebase 身份驗證錯誤',
      message: 'Google 登入驗證失敗',
      details: error.message || error.code,
      suggestions: [
        '嘗試重新登入',
        '檢查 Google 帳號狀態',
        '清除瀏覽器 Cookie',
        '使用無痕模式嘗試'
      ],
      type: 'auth'
    };
  }

  // 一般 JavaScript 錯誤
  if (error instanceof Error) {
    return {
      title: 'JavaScript 執行錯誤',
      message: error.message,
      details: error.stack?.split('\n')[1] || '詳細資訊請查看瀏覽器控制台',
      suggestions: [
        '重新整理頁面',
        '清除瀏覽器快取',
        '嘗試使用其他瀏覽器',
        '聯繫技術支援'
      ],
      type: 'unknown'
    };
  }

  // 未知錯誤
  return {
    title: '未知錯誤',
    message: '發生了預期外的錯誤',
    details: typeof error === 'string' ? error : JSON.stringify(error, null, 2),
    suggestions: [
      '重新整理頁面',
      '檢查瀏覽器控制台錯誤訊息',
      '聯繫系統管理員',
      '提供錯誤詳細資訊以便調查'
    ],
    type: 'unknown'
  };
}

export function getErrorIcon(type: DetailedError['type']): string {
  switch (type) {
    case 'network': return '🌐';
    case 'auth': return '🔐';
    case 'cors': return '🚫';
    case 'server': return '⚠️';
    default: return '❌';
  }
}